/**
 * Find on sheet (W44): where a word sits on the page, from the pdf.js text layer of the revision's page. Boxes are page
 * fractions (0–1 from the top-left), the frame every viewer marker uses (R19: pdf.js default viewport). Pure, so karma
 * can test it without a PDF.
 */
export interface PdfTextItem {
  str: string;
  /** [a, b, c, d, e, f]: the text's matrix in PDF user space */
  transform: number[];
  /** Advance width and height in user space */
  width: number;
  height: number;
}

export interface PdfViewportLike {
  width: number;
  height: number;
  convertToViewportPoint(x: number, y: number): number[];
}

export interface TextBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Every case-insensitive occurrence of `query` in the page's text items. A match inside an item takes its share of
 * the item's width (pdf.js gives no per-glyph positions); rotated text is boxed by its corners.
 */
export function findTextBoxes(items: PdfTextItem[], viewport: PdfViewportLike, query: string): TextBox[] {
  const needle = String(query ?? '').trim().toLowerCase();
  if (!needle || !viewport?.width || !viewport?.height) {
    return [];
  }
  const boxes: TextBox[] = [];
  for (const item of items ?? []) {
    const text = String(item?.str ?? '');
    const hay = text.toLowerCase();
    if (!hay || !item.transform || item.transform.length < 6) {
      continue;
    }
    const [a, b, c, d, e, f] = item.transform;
    const along = Math.hypot(a, b) || 1;
    const up = Math.hypot(c, d) || 1;
    const ux = (a / along) * item.width;
    const uy = (b / along) * item.width;
    const vx = (c / up) * item.height;
    const vy = (d / up) * item.height;
    for (let at = hay.indexOf(needle); at >= 0; at = hay.indexOf(needle, at + needle.length)) {
      const t0 = at / text.length;
      const t1 = (at + needle.length) / text.length;
      const x0 = e + ux * t0;
      const y0 = f + uy * t0;
      const x1 = e + ux * t1;
      const y1 = f + uy * t1;
      const corners = [[x0, y0], [x1, y1], [x0 + vx, y0 + vy], [x1 + vx, y1 + vy]].map(([x, y]) => viewport.convertToViewportPoint(x, y));
      const xs = corners.map(p => p[0]);
      const ys = corners.map(p => p[1]);
      const left = Math.min(...xs);
      const top = Math.min(...ys);
      boxes.push({
        x: left / viewport.width,
        y: top / viewport.height,
        w: (Math.max(...xs) - left) / viewport.width,
        h: (Math.max(...ys) - top) / viewport.height,
      });
    }
  }
  // Reading order: top to bottom, then left to right
  return boxes.sort((p, q) => (Math.abs(p.y - q.y) > 0.005 ? p.y - q.y : p.x - q.x));
}
